import { useState } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useClientPoint,
  FloatingPortal
} from "@floating-ui/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { UnavailableIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components";
import { UNAVAILABLE_HATCH_STYLE } from "./constants";

interface UnavailableRegionProps {
  top: number;
  height: number;
  title: string;
  subtitle: string | null;
}

// Hatched unavailable block with a hover/focus tooltip. The tooltip follows the
// cursor (useClientPoint) instead of anchoring to the block: these blocks can
// span many hours and their top edge is often scrolled off-screen, so an
// element-anchored tooltip flew up to the top of the page (QA: "хинт уплыл
// наверх"). flip + shift keep it inside the viewport near the pointer.
export const UnavailableRegion = ({
  top,
  height,
  title,
  subtitle
}: UnavailableRegionProps) => {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "top",
    whileElementsMounted: autoUpdate,
    middleware: [offset(12), flip({ padding: 8 }), shift({ padding: 8 })]
  });

  // axis "y" pins the tooltip to the block horizontally (its center) while
  // following the cursor vertically — steadier than tracking both axes across a
  // full-width column, and still always beside the hovered row.
  const clientPoint = useClientPoint(context, { axis: "y" });
  const hover = useHover(context);
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
    clientPoint
  ]);

  return (
    <>
      <div
        ref={refs.setReference}
        tabIndex={0}
        aria-label={subtitle ? `${title}. ${subtitle}` : title}
        className="absolute inset-x-0 outline-none"
        style={{ top, height, ...UNAVAILABLE_HATCH_STYLE }}
        {...getReferenceProps()}
      />
      <Show if={open}>
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="pointer-events-none z-50 flex max-w-[280px] items-center gap-4 rounded-lg border border-alpha-100 bg-white p-4 shadow-lg"
          >
            <HugeiconsIcon
              icon={UnavailableIcon}
              size={20}
              stroke="1.5px"
              className="shrink-0 text-content-400"
            />
            <div className="flex flex-col gap-1">
              <p className="text-[13px] font-medium leading-4 text-content-1000">
                {title}
              </p>
              <Show if={Boolean(subtitle)}>
                <p className="text-[13px] font-medium leading-4 text-content-200">
                  {subtitle}
                </p>
              </Show>
            </div>
          </div>
        </FloatingPortal>
      </Show>
    </>
  );
};
